/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { BalanceAdjustmentForm } from '../models/BalanceAdjustmentForm';
import type { BankPayAccountValidateInvalidateForm } from '../models/BankPayAccountValidateInvalidateForm';
import type { GiftBalanceAdjustmentForm } from '../models/GiftBalanceAdjustmentForm';
import type { GiftBalanceAdjustmentSalesManagementResponse } from '../models/GiftBalanceAdjustmentSalesManagementResponse';
import type { PageVoid } from '../models/PageVoid';
import type { PageWalletUserIndexResponse } from '../models/PageWalletUserIndexResponse';
import type { WalletUserBalanceDetailResponse } from '../models/WalletUserBalanceDetailResponse';
import type { WalletUserDetailResponse } from '../models/WalletUserDetailResponse';
import type { WalletUserEditForm } from '../models/WalletUserEditForm';
import type { WalletUserGiftBalanceDetailResponse } from '../models/WalletUserGiftBalanceDetailResponse';
import type { WalletUserSearchForm } from '../models/WalletUserSearchForm';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class WalletUserRepository {

  /**
   * ウォレットユーザ一覧表示
   * @returns PageWalletUserIndexResponse OK
   * @throws ApiError
   */
  public static index({
    walletUserId,
    externalUserIds,
    statuses,
    phoneNumber,
    email,
    createdAtStartDateTime,
    createdAtEndDateTime,
    lockStatuses,
    issuerNames,
    walletServiceNames,
    page = 1,
    size = 20,
    sort,
  }: {
    /**
     * ユーザID(複数指定不可)
     */
    walletUserId: string | undefined,
    /**
     * 外部ユーザID（複数指定可能）
     */
    externalUserIds: Array<string> | undefined,
    /**
     * ステータス（複数指定可能）
     */
    statuses: Array<'AVAILABLE' | 'STOP' | 'WITHDRAWAL'> | undefined,
    /**
     * 電話番号（複数指定不可）
     */
    phoneNumber: string | undefined,
    /**
     * メールアドレス(複数指定不可)
     */
    email: string | undefined,
    /**
     * 登録日時(開始日)　※msまでの日時が検索に反映される
     */
    createdAtStartDateTime: string | undefined,
    /**
     * 登録日時(終了日)　※msまでの日時が検索に反映される
     */
    createdAtEndDateTime: string | undefined,
    /**
     * アカウントロック状態(複数指定可能)
     */
    lockStatuses: Array<'AVAILABLE' | 'LOCKED'> | undefined,
    /**
     * イシュア名(複数指定可能)
     */
    issuerNames: Array<string> | undefined,
    /**
     * ウォレットサービス名(複数指定可能)
     */
    walletServiceNames: Array<string> | undefined,
    /**
     * One-based page index (1..N)
     */
    page: number | undefined,
    /**
     * The size of the page to be returned
     */
    size: number | undefined,
    /**
     * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
     */
    sort: Array<string> | undefined,
  }): CancelablePromise<PageWalletUserIndexResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/wallet_user',
      query: {
        'walletUserId': walletUserId,
        'externalUserIds': externalUserIds,
        'statuses': statuses,
        'phoneNumber': phoneNumber,
        'email': email,
        'createdAtStartDateTime': createdAtStartDateTime,
        'createdAtEndDateTime': createdAtEndDateTime,
        'lockStatuses': lockStatuses,
        'issuerNames': issuerNames,
        'walletServiceNames': walletServiceNames,
        'page': page,
        'size': size,
        'sort': sort,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * ウォレットユーザ残高詳細表示
   * @returns WalletUserBalanceDetailResponse OK
   * @throws ApiError
   */
  public static balance({
    walletUserId,
    paymentMethodId,
    walletServiceId,
  }: {
    /**
     * ウォレットユーザID
     */
    walletUserId: string,
    /**
     * 決済手段ID
     */
    paymentMethodId: string,
    /**
     * ウォレットサービスID
     */
    walletServiceId: string,
  }): CancelablePromise<WalletUserBalanceDetailResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/wallet_user/balance/{walletUserId}/{paymentMethodId}/{walletServiceId}',
      path: {
        'walletUserId': walletUserId,
        'paymentMethodId': paymentMethodId,
        'walletServiceId': walletServiceId,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * ウォレットユーザ商品券残高詳細表示
   * @returns WalletUserGiftBalanceDetailResponse OK
   * @throws ApiError
   */
  public static balance1({
    walletUserId,
    paymentMethodId,
    walletServiceId,
  }: {
    /**
     * ウォレットユーザID
     */
    walletUserId: string,
    /**
     * 決済手段ID (payment_type=gift_certificate)
     */
    paymentMethodId: string,
    /**
     * ウォレットサービスID
     */
    walletServiceId: string,
  }): CancelablePromise<WalletUserGiftBalanceDetailResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/wallet_user/gift_balance_detail/{walletUserId}/{paymentMethodId}/{walletServiceId}',
      path: {
        'walletUserId': walletUserId,
        'paymentMethodId': paymentMethodId,
        'walletServiceId': walletServiceId,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * ウォレットユーザ一覧のダウンロード予約
   * @returns any OK
   * @throws ApiError
   */
  public static walletUserIndexDownloadReserve({
    requestBody,
  }: {
    requestBody: WalletUserSearchForm,
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/wallet_user/download',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * ウォレットユーザ退会
   * @returns any OK
   * @throws ApiError
   */
  public static resign({
    id,
  }: {
    id: string,
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/wallet_user/{id}',
      path: {
        'id': id,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * ウォレットユーザ詳細表示
   * @returns WalletUserDetailResponse OK
   * @throws ApiError
   */
  public static detail({
    id,
  }: {
    /**
     * ウォレットユーザID
     */
    id: string,
  }): CancelablePromise<WalletUserDetailResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/wallet_user/{id}',
      path: {
        'id': id,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * ウォレットユーザ編集
   * @returns any OK
   * @throws ApiError
   */
  public static edit({
    id,
    requestBody,
  }: {
    id: string,
    requestBody: WalletUserEditForm,
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/wallet_user/{id}',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * ウォレットユーザ強制ログアウト
   * @returns any OK
   * @throws ApiError
   */
  public static logout({
    id,
  }: {
    id: string,
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/wallet_user/{id}/logout',
      path: {
        'id': id,
      },
      errors: {
        400: `Bad Request`,
      },
    });
  }

  /**
   * ウォレットユーザ利用停止
   * @returns any OK
   * @throws ApiError
   */
  public static stop({
    id,
  }: {
    id: string,
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/wallet_user/{id}/stop',
      path: {
        'id': id,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * ウォレットユーザ利用停止解除
   * @returns any OK
   * @throws ApiError
   */
  public static stopCancel({
    id,
  }: {
    id: string,
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/wallet_user/{id}/stop_cancel',
      path: {
        'id': id,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * ウォレットユーザログインロック解除
   * @returns any OK
   * @throws ApiError
   */
  public static unlock({
    id,
  }: {
    id: string,
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/wallet_user/{id}/unlock',
      path: {
        'id': id,
      },
      errors: {
        400: `Bad Request`,
      },
    });
  }

  /**
   * ウォレットユーザ 残高調整
   * @returns PageVoid OK
   * @throws ApiError
   */
  public static index1({
    walletUserId,
    requestBody,
  }: {
    walletUserId: string,
    requestBody: BalanceAdjustmentForm,
  }): CancelablePromise<PageVoid> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/wallet_user/{walletUserId}/balance',
      path: {
        'walletUserId': walletUserId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
      },
    });
  }

  /**
   * ウォレットユーザ BankPay口座有効化/無効化
   * @returns PageVoid OK
   * @throws ApiError
   */
  public static index2({
    walletUserId,
    valid,
    requestBody,
  }: {
    walletUserId: string,
    /**
     *
     * 有効化/無効化フラグ
     *
     * 有効化/無効化の指定. 有効化したい場合true, 無効化したい場合falseを指定する."
     *
     */
    valid: boolean,
    requestBody: BankPayAccountValidateInvalidateForm,
  }): CancelablePromise<PageVoid> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/wallet_user/{walletUserId}/bankpay_account/{valid}',
      path: {
        'walletUserId': walletUserId,
        'valid': valid,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
      },
    });
  }

  /**
   * ウォレットユーザ 商品券残高調整
   * @returns PageVoid OK
   * @throws ApiError
   */
  public static index3({
    walletUserId,
    requestBody,
  }: {
    walletUserId: string,
    requestBody: GiftBalanceAdjustmentForm,
  }): CancelablePromise<PageVoid> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/wallet_user/{walletUserId}/gift_balance_adjustment',
      path: {
        'walletUserId': walletUserId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
      },
    });
  }

  /**
   * 商品券残高調整(加算)用 販売管理一覧
   * @returns GiftBalanceAdjustmentSalesManagementResponse OK
   * @throws ApiError
   */
  public static index4({
    walletUserId,
    paymentMethodId,
  }: {
    walletUserId: string,
    paymentMethodId: number,
  }): CancelablePromise<Array<GiftBalanceAdjustmentSalesManagementResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/wallet_user/{walletUserId}/gift_balance_adjustment/sales_managements',
      path: {
        'walletUserId': walletUserId,
      },
      query: {
        'paymentMethodId': paymentMethodId,
      },
      errors: {
        404: `Not Found`,
      },
    });
  }

}
